import { marketColor } from './colors';
import { monthlyStanding, rankDelta, humanizeResourceName } from './paneData';

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

const metricLabel = (track) => (track === 'probabilistic' ? 'MWI' : 'RMSE');

const LeagueBadge = ({ league }) => {
  if (!league) return null;
  const missed = league.n_days_w_penalties ?? 0;
  const unqualified = league.league_id === 'unqualified';
  const cls = unqualified
    ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50'
    : missed > 0
      ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/50';
  const text = unqualified ? 'Unqualified' : missed > 0 ? `${missed} missed` : 'Qualified';
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}>{text}</span>;
};

// One row per market so the standings scale vertically with the field instead
// of shrinking each market into a sliver. Numbers stay light mono/tabular per
// VISUAL_IDENTITY §1.5; rows are separated by hairlines (§6).
const StandingsCard = ({ pane, userId, visibleResources }) => {
  const dark = isDark();
  const metric = metricLabel(pane.track);
  const metricHelp = pane.track === 'probabilistic'
    ? 'Mean Winkler interval score this month (lower is better)'
    : 'Root mean square error this month (lower is better)';
  const COL_HELP = {
    market: 'Market the forecaster competes in',
    rank: "Standing this month, ranked by the month's average error (best = #1)",
    delta: 'Change in daily rank since the previous scored day (▲ up, ▼ down)',
    metric: metricHelp,
    league: 'League qualification status for the month',
    sessions: 'Days submitted / days in the qualification window · participation rate',
  };
  return (
    <section aria-label="Standings">
      <div className="flex items-baseline justify-between">
        <h5 className="text-[11px] font-mono uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 font-normal">
          Standings
        </h5>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">Month-to-date</span>
      </div>
      <table className="mt-3 w-full table-fixed text-xs">
        <thead>
          <tr className="text-[10px] font-mono uppercase tracking-[0.1em] text-gray-400 dark:text-gray-500 [&>th]:cursor-help">
            {/* Δ 1d and Sessions are secondary detail; below sm the pane is too
                narrow for six nowrap columns, so they are dropped and the four
                core columns rebalanced. */}
            <th className="w-[30%] pb-2 pr-3 text-left font-normal" title={COL_HELP.market}>Market</th>
            <th className="w-[26%] sm:w-[20%] pb-2 px-3 text-left font-normal" title={COL_HELP.rank}>Monthly rank</th>
            <th className="hidden sm:table-cell sm:w-[10%] pb-2 px-3 text-right font-normal" title={COL_HELP.delta}>Δ&nbsp;1d</th>
            <th className="w-[18%] sm:w-[14%] pb-2 px-3 text-right font-normal" title={COL_HELP.metric}>{metric}</th>
            <th className="w-[26%] sm:w-[14%] pb-2 px-3 text-left font-normal" title={COL_HELP.league}>League</th>
            <th className="hidden sm:table-cell sm:w-[12%] pb-2 pl-3 text-right font-normal" title={COL_HELP.sessions}>Sessions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900/10 dark:divide-gray-100/10">
          {visibleResources.map((x, i) => {
            const standing = monthlyStanding(x.data.series, userId);
            const delta = rankDelta(x.data.series, userId);
            // A monthly-stats row with a null league_id carries no real league
            // data (probabilistic-only user, stats not yet computed, or the
            // manager fallback row) - treat it the same as "no league".
            const league = x.data.leagueStatus === 'ok' && x.data.leagueUserId === String(userId)
              && x.data.league?.league_id != null
              ? x.data.league
              : null;
            const participation = league
              ? Math.round(league.participation_rate ?? ((league.days_with_submissions / league.days_in_period) * 100))
              : null;
            const displayName = humanizeResourceName(x.resource.name);
            return (
              <tr key={x.resource.id} className="pane-row-in text-gray-700 dark:text-gray-300" style={{ animationDelay: `${i * 45}ms` }}>
                <td className="py-2 pr-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="h-2 w-2 flex-shrink-0 rounded-sm" style={{ background: marketColor(x.index, dark) }} />
                    <span className="truncate font-medium" title={displayName}>{displayName}</span>
                  </span>
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  {standing ? (
                    <>
                      <span className="font-mono tabular-nums text-sm text-gray-900 dark:text-gray-100">#{standing.rank}</span>
                      <span className="text-gray-400 dark:text-gray-500"> of {standing.of}</span>
                    </>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">—</span>
                  )}
                </td>
                <td className="hidden sm:table-cell py-2 px-3 text-right whitespace-nowrap">
                  {delta != null && delta !== 0 ? (
                    <span
                      className={`font-mono tabular-nums ${
                        delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
                    </span>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">–</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right whitespace-nowrap font-mono tabular-nums text-gray-600 dark:text-gray-300">
                  {standing ? standing.avgError.toFixed(1) : '—'}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <LeagueBadge league={league} />
                </td>
                <td className="hidden sm:table-cell py-2 pl-3 text-right whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                  {league ? `${league.days_with_submissions}/${league.days_in_period} · ${participation}%` : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

export default StandingsCard;
